/**
 * LA CARTE — la signature du produit (design §3).
 *
 * Parchemin, hachures par biome, tracé à la plume, rose des vents. Trois états de brouillard qui se
 * distinguent AU PREMIER COUP D'ŒIL : ce qui est inconnu n'est pas dans la réponse du serveur (un
 * brouillard qu'on peut lire dans le JSON n'est pas un brouillard), ce qui est aperçu est une
 * silhouette au trait pointillé, ce qui est visité est dessiné, nommé, et porte son coût.
 *
 * L'hexagone où l'on se trouve est marqué d'une croix à la plume — pas d'une épingle : une épingle,
 * c'est une carte routière.
 *
 * ELLE DESSINE, ELLE NE DÉCIDE PAS. Les annotations lui arrivent DÉJÀ TRIÉES — filtre appliqué,
 * calque éteint ou allumé (§4.2). Deux raisons : le filtre se règle sous la carte et se lit aussi
 * dans la liste des affaires en suspens, qui n'est pas ici ; et une carte qui filtrerait elle-même
 * serait une carte à qui il faudrait redire ses règles à chaque endroit où on la pose.
 *
 * CE QU'ON NE FAIT TOUJOURS PAS : se déplacer en cliquant. On voyage par les choix du nœud. Cliquer
 * une case, c'est y écrire — rien d'autre.
 */
import { useMemo } from "react";

import type { Annotation, CarteEnVue, HexagoneFrequente } from "../api/types.js";
import { disposeLaCarte } from "../carte/hexagones.js";
import { BIOMES, MARQUEURS_DITS, dureeDite } from "../lexique/mots.js";
import { hachureDe } from "./Defs.js";
import { traceDe } from "./Glyphe.js";
import { t, tn } from "../lexique/langue.js";

const TAILLE = 34;

/**
 * OÙ POINTE L'AIGUILLE (§4.5), en degrés depuis le nord, pour les six directions d'une grille
 * hexagonale pointe en haut. Le serveur rend un CODE ; la géométrie est une affaire de dessin, donc
 * elle vit ici — et le jour où la carte tournera, il n'y aura qu'une table à changer.
 */
const DEGRES_DU_CAP: Record<string, number> = {
  est: 90,
  nord_est: 30,
  nord_ouest: 330,
  ouest: 270,
  sud_ouest: 210,
  sud_est: 150
};
/** Trois marqueurs par case, et le compte du reste. Au-delà, on ne dessine plus une carte : on
 * empile des signes sur un hexagone de soixante pixels, et plus rien ne se lit. */
const MARQUEURS_PAR_CASE = 3;

interface Posee {
  annotation: Annotation;
  mienne: boolean;
}

export function CarteDuMonde({
  carte,
  hauteur = 460,
  miennes = [],
  calque = [],
  frequentation = [],
  choisi = null,
  designe = null,
  surChoix,
  surSurvol
}: {
  carte: CarteEnVue;
  hauteur?: number;
  /** Les siennes, en encre pleine. Déjà filtrées. */
  miennes?: Annotation[];
  /** Celles des autres (§Q21), en encre pâle. Déjà filtrées, et vides si le calque est éteint. */
  calque?: Annotation[];
  /** LE TROISIÈME CALQUE (§4.4) : où l'on est mort. Une petite croix, à part des marqueurs du
   * joueur et des notes publiées — trois calques superposés sur une carte de mille nœuds, c'est
   * deux de trop si on ne peut pas les couper. Vide quand il est éteint. */
  frequentation?: HexagoneFrequente[];
  choisi?: string | null;
  /** LA CASE QU'ON DÉSIGNE D'AILLEURS — depuis une ligne de note survolée, sous la carte. Elle bat
   * deux fois, doucement, et s'arrête avec le geste : c'est la seule animation de la carte qui ne
   * raconte pas l'exploration, elle RÉPOND. Sans ce chemin-là, une note et sa case n'ont en commun
   * qu'une clef écrite d'un seul côté (design §3). */
  designe?: string | null;
  surChoix?: (clef: string) => void;
  /** La case sous la main, ou `null` quand on en sort. C'est ce qui remplit le cartouche de
   * lecture ; la carte, elle, ne garde rien et ne décide rien. */
  surSurvol?: (clef: string | null) => void;
}) {
  const { cases, vue } = useMemo(() => disposeLaCarte(carte.hexagones, TAILLE), [carte.hexagones]);
  const morts = useMemo(
    () => new Map(frequentation.filter((h) => h.morts > 0).map((h) => [h.clef, h])),
    [frequentation]
  );

  // LES ANNOTATIONS RANGÉES PAR CASE, LES SIENNES D'ABORD. Quand une case en porte plus qu'elle ne
  // peut en montrer, ce sont donc les siennes qu'on voit — sa propre carte passe avant celle des
  // autres, toujours.
  const parCase = useMemo(() => {
    const table = new Map<string, Posee[]>();
    const range = (annotation: Annotation, mienne: boolean) => {
      const liste = table.get(annotation.hexClef) ?? [];
      liste.push({ annotation, mienne });
      table.set(annotation.hexClef, liste);
    };
    for (const a of miennes) range(a, true);
    for (const a of calque) range(a, false);
    return table;
  }, [miennes, calque]);

  if (!cases.length) {
    return (
      <p className="douce">
        {t("La carte est vierge. Elle se dessinera au fur et à mesure — un hexagone n'existe que lorsqu'on l'a vu.")}
      </p>
    );
  }

  return (
    <figure className="carte-cadre" style={{ maxHeight: hauteur }}>
      <svg
        className="carte"
        viewBox={`${vue.x} ${vue.y} ${vue.largeur} ${vue.hauteur}`}
        style={{ maxHeight: hauteur }}
        role="img"
        aria-label={`Carte : ${cases.length} hexagones connus`}
      >
        {/* Le cadre à la plume : deux filets, l'un plus épais, comme sur une carte gravée. */}
        <rect
          className="carte__cadre"
          x={vue.x + 4}
          y={vue.y + 4}
          width={vue.largeur - 8}
          height={vue.hauteur - 8}
        />

        {cases.map(({ hex, cx, cy, points }) => {
          const visite = hex.brouillard === "visite";
          const ici = carte.ici === hex.clef;
          const biome = BIOMES[hex.biome];
          const posees = parCase.get(hex.clef) ?? [];
          const montrees = posees.slice(0, MARQUEURS_PAR_CASE);
          const reste = posees.length - montrees.length;
          const classesDeCase = ["carte__case"];
          if (choisi === hex.clef) classesDeCase.push("carte__case--choisie");
          if (designe === hex.clef) classesDeCase.push("carte__case--designee");
          return (
            <g key={hex.clef} className={classesDeCase.join(" ")}>
              <polygon className={visite ? "carte__hex" : "carte__hex carte__hex--apercu"} points={points}>
                <title>
                  {/* LE REPÈRE SE LIT DANS LES DEUX ÉTATS (§4.5), et c'est tout son emploi : sur une
                      case APERÇUE, c'est la seule chose qu'on sache d'elle — « la tour brisée,
                      là-bas » —, et c'est là-dessus qu'on choisit une direction. Le NOM et le coût
                      de traversée restent réservés aux cases visitées : eux s'apprennent en y
                      allant. */}
                  {visite
                    ? [
                        t("{lieu} — {biome}, traversée {duree}", {
                          lieu: hex.nom ?? hex.clef,
                          biome: biome?.nom ?? hex.biome,
                          duree: dureeDite(hex.coutHeures ?? 0)
                        }),
                        hex.repere ? t("On la reconnaît à {repere}.", { repere: hex.repere }) : null,
                        hex.sanctuaire ? t("Sanctuaire : on y dort, on s'y fait soigner.") : null
                      ]
                        .filter(Boolean)
                        .join("\n")
                    : t("Aperçu — {biome}{repere}", {
                        biome: biome?.nom ?? hex.biome,
                        repere: hex.repere ? ` : ${hex.repere}` : ""
                      })}
                </title>
              </polygon>
              <polygon
                className={visite ? "carte__trame" : "carte__trame--apercu carte__trame"}
                points={points}
                fill={hachureDe(hex.biome)}
              />
              {visite && hex.nom ? (
                <text className="carte__nom" x={cx} y={cy + TAILLE * 0.62}>
                  {hex.nom.length > 16 ? `${hex.nom.slice(0, 15)}…` : hex.nom}
                </text>
              ) : null}
              {/* LE SANCTUAIRE (§3.4) — un toit dessiné à la plume, et rien d'autre. Il n'a pas de
                  marqueur d'annotation : ce n'est pas une note du joueur, c'est un fait du monde,
                  et l'on doit pouvoir le distinguer de ce qu'on a écrit soi-même. */}
              {visite && hex.sanctuaire ? (
                <path
                  className="carte__sanctuaire"
                  d={`M${cx - 7} ${cy + 4}v-5l7-6 7 6v5z`}
                  aria-hidden="true"
                />
              ) : null}
              {ici ? (
                <g className="carte__ici">
                  {/* Une croix tracée à la main, et le cercle qui l'entoure — « je suis ici ». */}
                  <path d={`M${cx - 8} ${cy - 8}l16 16M${cx + 8} ${cy - 8}l-16 16`} />
                  <circle cx={cx} cy={cy} r={TAILLE * 0.52} strokeDasharray="4 4" />
                </g>
              ) : null}

              {/* LES MARQUEURS, POSÉS EN HAUT DE LA CASE — au-dessus du nom, jamais dessus. Chacun
                  a SA FORME (§4.2) : la couleur ne porte pas l'information, et la carte doit rester
                  lisible imprimée en noir. Les siennes sont pleines, celles du calque plus fines. */}
              {montrees.map(({ annotation, mienne }, rang) => {
                const trace = traceDe(MARQUEURS_DITS[annotation.marqueur].glyphe);
                const largeur = 15;
                const x = cx + (rang - (montrees.length - 1) / 2) * (largeur + 2) - largeur / 2;
                const y = cy - TAILLE * 0.44;
                const classes = ["carte__marqueur"];
                if (!mienne) classes.push("carte__marqueur--calque");
                if (annotation.resolue) classes.push("carte__marqueur--resolue");
                return (
                  <g
                    key={annotation.id}
                    className={classes.join(" ")}
                    transform={`translate(${x} ${y}) scale(${largeur / 24})`}
                  >
                    <path d={trace.d} />
                    <title>
                      {MARQUEURS_DITS[annotation.marqueur].nom}
                      {annotation.note ? ` — ${annotation.note}` : ""}
                      {mienne ? "" : ` (${annotation.heros ?? t("un autre héros")})`}
                    </title>
                  </g>
                );
              })}
              {reste > 0 ? (
                <text className="carte__reste nombre" x={cx + TAILLE * 0.52} y={cy - TAILLE * 0.36}>
                  +{reste}
                </text>
              ) : null}

              {/* LÀ OÙ D'AUTRES SONT TOMBÉS (§4.4). Une croix, en bas de la case, loin des
                  marqueurs qui sont en haut : c'est une information d'une autre nature, et elle ne
                  doit pas se confondre avec ce qu'on a soi-même noté. Le nombre ne s'écrit qu'à
                  partir de deux — « 1 » à côté d'une croix ne dit rien de plus que la croix. */}
              {(() => {
                const tombes = morts.get(hex.clef);
                if (!tombes) return null;
                return (
                  <g className="carte__tombes" transform={`translate(${cx - TAILLE * 0.5} ${cy + TAILLE * 0.18})`}>
                    <path d="M6 0v11M2.5 3.4h7" />
                    {tombes.morts > 1 ? (
                      <text className="carte__reste nombre" x={11} y={10}>
                        {tombes.morts}
                      </text>
                    ) : null}
                    <title>
                      {tn(tombes.morts, "{n} héros tombé ici", "{n} héros tombés ici")}
                      {tombes.survie
                        ? t(" — {pourcent} % en sont repartis vivants", { pourcent: tombes.survie.pourcent })
                        : ""}
                    </title>
                  </g>
                );
              })()}

              {/* LA ZONE DE CLIC EST LA DERNIÈRE : posée par-dessus le dessin, elle prend le clic
                  sans que l'ordre des tracés ait à s'en soucier. Elle est focusable au clavier —
                  une carte qu'on n'annote qu'à la souris est une carte qu'une partie des joueurs
                  ne peut pas annoter du tout. */}
              {surChoix || surSurvol ? (
                <polygon
                  className={surChoix ? "carte__prise" : "carte__prise carte__prise--muette"}
                  points={points}
                  tabIndex={surChoix ? 0 : undefined}
                  role={surChoix ? "button" : undefined}
                  aria-label={surChoix ? t("Écrire sur {lieu}", { lieu: hex.nom ?? hex.clef }) : undefined}
                  onClick={surChoix ? () => surChoix(hex.clef) : undefined}
                  // LE SURVOL PASSE PAR LA MÊME PRISE QUE LE CLIC. Poser les écouteurs sur le
                  // dessin ferait clignoter le cartouche entre l'hexagone, sa trame et ses
                  // marqueurs — trois formes empilées, trois entrées et trois sorties par case.
                  onMouseEnter={surSurvol ? () => surSurvol(hex.clef) : undefined}
                  onMouseLeave={surSurvol ? () => surSurvol(null) : undefined}
                  // AU CLAVIER AUSSI : le focus vaut le survol, sans quoi le cartouche n'existe
                  // que pour qui a une souris (§5).
                  onFocus={surSurvol ? () => surSurvol(hex.clef) : undefined}
                  onBlur={surSurvol ? () => surSurvol(null) : undefined}
                  onKeyDown={(evenement) => {
                    if (surChoix && (evenement.key === "Enter" || evenement.key === " ")) {
                      evenement.preventDefault();
                      surChoix(hex.clef);
                    }
                  }}
                />
              ) : null}
            </g>
          );
        })}

        {/* La rose des vents, en bas à droite : quatre branches, une pointe pleine au nord. */}
        <g transform={`translate(${vue.x + vue.largeur - 54} ${vue.y + vue.hauteur - 54})`}>
          <circle className="carte__rose" cx="0" cy="0" r="22" />
          <path className="carte__rose" d="M0 -30V30M-30 0H30" />
          <path className="carte__rose-pleine" d="M0 -30l6 22-6 8-6-8z" />
          <text className="carte__nom" x="0" y="-34">
            {t("N")}
          </text>
          {/* L'AIGUILLE (§4.5), et seulement si l'on porte une boussole. Elle se pose SUR la rose
              plutôt qu'à côté : c'est la même chose qu'une rose des vents — une direction — et lui
              donner son propre cadre en ferait un panneau d'interface de plus. Elle ne dit pas ce
              qu'il y a là-bas ; elle dit de quel côté, et à combien de cases. */}
          {carte.boussole ? (
            <g
              className="carte__aiguille"
              transform={`rotate(${DEGRES_DU_CAP[carte.boussole.direction] ?? 0})`}
              aria-hidden="true"
            >
              <path d="M0 0V-19" />
              <path d="M0 -19l4 6-4 3-4-3z" />
            </g>
          ) : null}
        </g>
      </svg>
    </figure>
  );
}
